import { EmptyState, Table, useDrawer } from '@iveco-ti/iveco-ds'
import { HeadProvider, Title } from 'react-head'
import { useTranslation } from 'react-i18next'

import { ExportDropdown } from '@/common/components/ExportDropdown'
import { FilterButton } from '@/common/components/FilterButton'
import { OrderByDropdown } from '@/common/components/OrderByDropdown'
import { useDownloadExport } from '@/common/hooks/useDownloadExport'
import { ApplicationAccountUserRow } from './components/ApplicationAccountUserRow'
import { ApplicationAccountUserRowSkeleton } from './components/ApplicationAccountUserRowSkeleton'
import { DrawerFilterApplicationAccountUser } from './components/DrawerFilterApplicationAccountUser'
import { DrawerManageApplicationAccountUser } from './components/DrawerManageApplicationAccountUser'
import { DrawerViewApplicationAccountUser } from './components/DrawerViewApplicationAccountUser'
import { usePaginateUserApplicationAccountUser } from './hooks/usePaginateUserApplicationAccountUser'

export default function Access() {
  const { t } = useTranslation(['sso', 'common'])
  const {
    handleUsersAccessSearch,
    userApplicationAccountUserOrderBy,
    handleChangeOrder,
    filtersQty,
    isLoadingUserApplicationAccountUser,
    userApplicationAccountUser,
    userApplicationAccountUserPaginated,
    handleChangePage,
    userApplicationAccountUserPerPage,
  } = usePaginateUserApplicationAccountUser()
  const { downloadExport, isDownloadingExport } = useDownloadExport({
    route: '/application-account-users/report',
  })
  const drawerFilterUsersAccess = useDrawer(
    'drawer-filter-application-account-user',
  )

  const ORDER_BY_OPTIONS = [
    {
      label: t('sso:access.table.order_dropdown.fields.name.label'),
      field: 'UserPreference.displayName',
    },
    {
      label: t('sso:access.table.order_dropdown.fields.email.label'),
      field: 'email',
    },
  ]

  const TABLE_COLUMNS = [
    {
      label: t('sso:access.table.table_head.column_first.label'),
      order: 'UserPreference.displayName',
    },
    {
      label: t('sso:access.table.table_head.column_second.label'),
    },
    {
      label: t('sso:access.table.table_head.column_third.label'),
      end: true,
    },
  ]

  return (
    <>
      <div className="overflow-y-auto h-full shadow-sm rounded-xl bg-white">
        <HeadProvider>
          <Title>{t('sso:access.browser.title')}</Title>
        </HeadProvider>

        <Table.Root>
          <Table.Search
            searchName="search"
            searchPlaceholder={t(
              'common:authenticated_layout.search_input.placeholder',
            )}
            onSearch={handleUsersAccessSearch}
          >
            <OrderByDropdown
              selected={userApplicationAccountUserOrderBy}
              onChangeOrder={handleChangeOrder}
              options={ORDER_BY_OPTIONS}
            />

            <ExportDropdown
              onExportCSV={() => downloadExport({}, 'csv')}
              onExportXLSX={() => downloadExport({}, 'xlsx')}
              isExporting={isDownloadingExport}
            />

            <FilterButton
              open={drawerFilterUsersAccess.open}
              filters={filtersQty}
            />
          </Table.Search>

          {isLoadingUserApplicationAccountUser && (
            <ApplicationAccountUserRowSkeleton />
          )}

          {!userApplicationAccountUser.length &&
            !isLoadingUserApplicationAccountUser && (
              <EmptyState
                title={t('sso:access.empty_state.title')}
                description={t('sso:access.empty_state.description')}
              />
            )}

          <Table.Content>
            {!isLoadingUserApplicationAccountUser &&
              !!userApplicationAccountUser.length && (
                <>
                  <Table.Head
                    hideCheckbox
                    cols={TABLE_COLUMNS}
                    onOrderBy={(order) => handleChangeOrder(order, 'asc')}
                  />

                  <Table.Body>
                    {userApplicationAccountUser.map((userAccess) => (
                      <ApplicationAccountUserRow
                        key={userAccess.id}
                        userAccess={userAccess}
                      />
                    ))}
                  </Table.Body>
                </>
              )}
          </Table.Content>

          <Table.Footer
            page={userApplicationAccountUserPaginated?.currentPage || 1}
            totalItems={userApplicationAccountUserPaginated?.totalItems || 0}
            totalItemsLabel={t(
              'sso:access.table.table_footer.total_registers.label',
            )}
            totalPages={userApplicationAccountUserPaginated?.totalPages || 1}
            ofText={t('common:components.table_footer.pagination.label')}
            onChangePage={handleChangePage}
            itemsPerPage={userApplicationAccountUserPerPage}
          />
        </Table.Root>
      </div>

      <DrawerFilterApplicationAccountUser />
      <DrawerViewApplicationAccountUser />
      <DrawerManageApplicationAccountUser />
    </>
  )
}
